// Any HUD panel can be folded away to a small labelled tab and opened again,
// and is remembered that way next time (per viewer, in localStorage).

const KEY = 'tidebreaker.minimized';

function load(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function save(names: Set<string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...names]));
  } catch {
    // Storage refused (private window and the like): it just won't be remembered.
  }
}

/** Give `panel` a fold button; folded, it shows only `title`. */
export function minimizable(panel: HTMLElement, name: string, title: string): void {
  const label = document.createElement('div');
  label.className = 'min-title';
  label.textContent = title;
  const btn = document.createElement('button');
  btn.className = 'min-btn';
  btn.type = 'button';
  panel.prepend(label);
  panel.appendChild(btn);
  const set = (min: boolean) => {
    panel.classList.toggle('min', min);
    btn.title = min ? `Show ${title.toLowerCase()}` : `Minimise ${title.toLowerCase()}`;
    btn.setAttribute('aria-label', btn.title);
    btn.setAttribute('aria-expanded', String(!min));
  };
  set(load().has(name));
  const toggle = (e: Event) => {
    e.stopPropagation();
    const names = load();
    const min = !panel.classList.contains('min');
    if (min) names.add(name);
    else names.delete(name);
    save(names);
    fold(panel, () => set(min));
  };
  btn.addEventListener('click', toggle);
  // The folded tab opens from anywhere on it.
  label.addEventListener('click', toggle);
}

/**
 * Make the change, then ease the panel from its old size to its new one
 * (it stays anchored where it is) while what it now shows fades in.
 */
function fold(panel: HTMLElement, change: () => void): void {
  const a = panel.getBoundingClientRect();
  change();
  const b = panel.getBoundingClientRect();
  if (!panel.animate || matchMedia('(prefers-reduced-motion: reduce)').matches || (a.width === b.width && a.height === b.height)) return;
  for (const x of panel.getAnimations()) x.cancel();
  panel.style.overflow = 'hidden';
  const grow = panel.animate(
    [{ width: `${a.width}px`, height: `${a.height}px` }, { width: `${b.width}px`, height: `${b.height}px` }],
    { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
  );
  const done = () => (panel.style.overflow = '');
  grow.onfinish = done;
  grow.oncancel = done;
  for (const c of Array.from(panel.children) as HTMLElement[]) {
    if (c.classList.contains('min-btn') || getComputedStyle(c).display === 'none') continue;
    c.animate([{ opacity: 0, transform: 'translateY(3px)' }, { opacity: 1, transform: 'none' }], { duration: 200, delay: 80, easing: 'ease-out', fill: 'backwards' });
  }
}
